import { useState, useEffect, useRef } from "react";

const HEADLINE_WORDS = ["ILLUMINATE", "YOUR", "WORKSPACE."];

const STATS = [
  { val: "10K+", lbl: "Customers"  },
  { val: "4.9",  lbl: "Avg Rating" },
  { val: "50+",  lbl: "Products"   },
  { val: "2 YR", lbl: "Warranty"   },
];


const SPOTS = [
  { id: "touch",    x: 29, y: 44, align: "right", title: "Touch On/Off",       specs: ["Tap top of shade", "3 Colour Temps", "Instant Response"] },
  { id: "magnetic", x: 43, y: 37, align: "left",  title: "Magnetic Ball Joint", specs: ["Drag to Rotate", "Click to Detach", "Magnetic Re-lock"] },
  { id: "base",     x: 57, y: 95, align: "left",  title: "CNC Aluminium Base",  specs: ["Aerospace Grade", "2.8 kg Ballast", "Brushed Satin"] },
];

const LIGHTS = [
  { label: "Cool White", rgb: "210,232,255", led: "#e8f2ff" },
  { label: "Warm White", rgb: "255,248,220", led: "#fff9e0" },
  { label: "Warm Amber", rgb: "255,200,60",  led: "#ffd54f" },
];

// SVG geometry — viewBox 300 × 420
const JX = 123, JY = 150;          // ball joint centre
const ROD_X = 158;                  // rod centre x
const BASE_CX = 158, BASE_CY = 406;
const D = 30, R = 32, ERX = 15;    // drum: depth, radius, ellipse-rx

export default function Hero() {
  const [vis,      setVis]      = useState(false);
  const [active,   setActive]   = useState(null);
  const [isOn,     setIsOn]     = useState(true);
  const [colorIdx, setColorIdx] = useState(0);
  const [angle,    setAngle]    = useState(-30);   // CSS rotate deg (neg = face down-left)
  const [detached, setDetached] = useState(false);
  const [dragging, setDragging] = useState(false);
  const timeoutRef = useRef(null);

  const svgRef  = useRef(null);
  const dragRef = useRef(null);  // { startAngle, startMAngle }
  const wasDrag = useRef(false); // true if user moved during this press

  useEffect(() => {
    const t = setTimeout(() => setVis(true), 60);
    return () => clearTimeout(t);
  }, []);

  const toSVG = (cx, cy) => {
    if (!svgRef.current) return { x: 0, y: 0 };
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: ((cx - rect.left) / rect.width)  * 300,
      y: ((cy - rect.top)  / rect.height) * 420,
    };
  };

  const onHeadDown = (e) => {
    if (detached) return;
    e.preventDefault();
    e.stopPropagation();
    const cl = e.touches ? e.touches[0] : e;
    const pt = toSVG(cl.clientX, cl.clientY);
    dragRef.current  = { startAngle: angle, startMAngle: Math.atan2(pt.y - JY, pt.x - JX) * (180 / Math.PI) };
    wasDrag.current  = false;
    setDragging(true);
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const cl = e.touches ? e.touches[0] : e;
      const pt = toSVG(cl.clientX, cl.clientY);
      let delta = Math.atan2(pt.y - JY, pt.x - JX) * (180 / Math.PI) - dragRef.current.startMAngle;
      if (delta >  180) delta -= 360;
      if (delta < -180) delta += 360;
      if (Math.abs(delta) > 1.5) wasDrag.current = true;
      setAngle(Math.max(-82, Math.min(-22, dragRef.current.startAngle + delta)));
    };
    const onUp = () => { dragRef.current = null; setDragging(false); };
    window.addEventListener("mousemove",  onMove);
    window.addEventListener("mouseup",    onUp);
    window.addEventListener("touchmove",  onMove, { passive: false });
    window.addEventListener("touchend",   onUp);
    return () => {
      window.removeEventListener("mousemove",  onMove);
      window.removeEventListener("mouseup",    onUp);
      window.removeEventListener("touchmove",  onMove);
      window.removeEventListener("touchend",   onUp);
    };
  }, []);

  // Light cone geometry
  const rad  = angle * (Math.PI / 180);
  const offset = 8; // 🔥 magic number

const faceX = JX - (D + offset) * Math.cos(rad);
const faceY = JY - (D + offset) * Math.sin(rad);
  const axX   = -Math.cos(rad);
  const axY   = -Math.sin(rad);
  const pX    =  axY;
  const pY    = -axX;

  const lc = LIGHTS[colorIdx];

  const headStyle = {
    transform: detached
      ? `translate(${JX + 74}px, ${JY - 70}px) rotate(-30deg)`
      : `translate(${JX}px, ${JY}px) rotate(${angle}deg)`,
    transformOrigin: "0px 0px",
    transition: dragging ? "none" : "transform 0.45s cubic-bezier(0.34,1.56,0.64,1)",
    cursor: detached ? "default" : dragging ? "grabbing" : "grab",
    userSelect: "none",
  };

  return (
    <section className={`h4${vis ? " vis" : ""}`  }>
      <div aria-hidden="true">
        <div className="h4-orb h4-orb1" />
        <div className="h4-orb h4-orb2" />
        <div className="h4-orb h4-orb3" />
        <div className="h4-grid" />
      </div>

      <div className="h4-content">
        {/* ── LEFT ── */}
        <div className="h4-left">
          <div className="h4-eyebrow">
            <span className="h4-eyebrow-dot" />
            Precision Desk Lighting
          </div>
          <h1 className="h4-headline">
            {HEADLINE_WORDS.map((w, i) => (
              <span key={i} className="h4-word">
                <span className="h4-word-in" style={{ transitionDelay: `${i * 0.13 + 0.14}s` }}>
                  {w}
                </span>
              </span>
            ))}
          </h1>
          <p className="h4-sub">
            Engineered for focus-driven professionals and creators.
            Every detail refined. Every lumen intentional.
          </p>
          <div className="h4-actions">
            <a href="#collection" className="h4-btn-pri">
              Shop Collection
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </a>
            <a href="#about" className="h4-btn-ghost">Learn More</a>
          </div>
          <div className="h4-trust">
            <span>Free Shipping</span><span className="h4-trust-sep" />
            <span>7-Day Returns</span><span className="h4-trust-sep" />
            <span>2-Year Warranty</span>
          </div>
        </div>

        {/* ── RIGHT — lamp ── */}
        <div className="h4-right">
          <div className="h4-lamp-wrap">

            {/* ─── SVG LAMP ─── */}
            <svg
              ref={svgRef}
              viewBox="0 0 300 420"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ width: "100%", height: "100%", display: "block" , overflow: "visible"}}
            >
              <defs>
                <filter id="lp-glow" x="-80%" y="-80%" width="260%" height="260%">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="b"/>
                  <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
                </filter>
                <filter id="lp-sg" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="b"/>
                  <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
                </filter>
                <linearGradient id="beamCore" gradientUnits="userSpaceOnUse"
                  x1={faceX} y1={faceY}
                  x2={faceX + axX * 600} y2={faceY + axY * 600}>
                  <stop offset="0%" stopColor={`rgba(${lc.rgb},0.9)`}/>
                  <stop offset="15%" stopColor={`rgba(${lc.rgb},0.6)`}/>
                  <stop offset="40%" stopColor={`rgba(${lc.rgb},0.25)`}/>
                  <stop offset="70%" stopColor={`rgba(${lc.rgb},0.08)`}/>
                  <stop offset="100%" stopColor="rgba(0,0,0,0)"/>
                </linearGradient>

                <radialGradient id="beamSoft">
                  <stop offset="0%" stopColor={`rgba(${lc.rgb},0.4)`}/>
                  <stop offset="100%" stopColor="rgba(0,0,0,0)"/>
                </radialGradient>

                <filter id="beamNoise">
                  <feTurbulence type="fractalNoise" baseFrequency="0.6" numOctaves="2" seed="2"/>
                  <feColorMatrix type="saturate" values="0"/>
                  <feComponentTransfer>
                    <feFuncA type="linear" slope="0.25"/>
                  </feComponentTransfer>
                </filter>
              </defs>

              {isOn && !detached && (
                <g style={{ mixBlendMode: "screen" }}>

                  {/* MAIN BEAM SHAPE */}
                  <path
                    d={`
                      M ${faceX + pX*18} ${faceY + pY*18}
                      L ${faceX + axX*700 + pX*140} ${faceY + axY*700 + pY*140}
                      L ${faceX + axX*700 - pX*140} ${faceY + axY*700 - pY*140}
                      L ${faceX - pX*18} ${faceY - pY*18}
                      Z
                    `}
                    fill="url(#beamCore)"
                    filter="url(#beamNoise)"
                    style={{ filter: "blur(12px)" }}
                  />

                  {/* INNER INTENSE CORE */}
                  <path
                    d={`
                      M ${faceX + pX*8} ${faceY + pY*8}
                      L ${faceX + axX*600 + pX*60} ${faceY + axY*600 + pY*60}
                      L ${faceX + axX*600 - pX*60} ${faceY + axY*600 - pY*60}
                      L ${faceX - pX*8} ${faceY - pY*8}
                      Z
                    `}
                    fill={`rgba(${lc.rgb},0.35)`}
                    style={{ filter: "blur(6px)" }}
                  />

                  {/* ORIGIN GLOW (lamp se nikalti hui light) */}
                  <ellipse
                    cx={faceX}
                    cy={faceY}
                    rx="35"
                    ry="28"
                    fill="url(#beamSoft)"
                    style={{ filter: "blur(10px)" }}
                  />

                </g>
              )}

              {/* ── BASE ── */}
              <ellipse cx={BASE_CX} cy={BASE_CY + 9} rx="64" ry="6"  fill="rgba(0,0,0,0.32)"/>
              <ellipse cx={BASE_CX} cy={BASE_CY}     rx="63" ry="8.5" fill="#0e0e0e"/>
              <ellipse cx={BASE_CX} cy={BASE_CY - 3} rx="61" ry="7"   fill="#1b1b1b"/>
              <ellipse cx={BASE_CX - 12} cy={BASE_CY - 5} rx="20" ry="2" fill="#272727" opacity=".5"/>
              <ellipse cx={BASE_CX}      cy={BASE_CY - 2} rx="61" ry="7" fill="none" stroke="#232323" strokeWidth="1"/>

              {/* ── ROD ── */}
              <rect x={ROD_X - 4} y={JY -2.5} width="6" height={BASE_CY - JY - 3} rx="2.5" fill="#111"/>
              <rect x={ROD_X - 1}   y={JY + 5} width="1" height={BASE_CY - JY - 3} rx="1"   fill="#222" opacity=".7"/>

              {/* ── T-ARM (hidden when detached) ── */}
              {!detached && (
                <>
                  <rect x={JX + 2} y={JY - 2.5} width={ROD_X - 2.5 - JX} height="5" rx="2.5" fill="#111"/>
                  <rect x={JX} y={JY - 1}   width={ROD_X - 2.5 - JX} height="2" rx="1"   fill="#222" opacity=".7"/>
                </>
              )}

              {/* ── DRUM HEAD ── */}
              <g style={headStyle} onMouseDown={onHeadDown} onTouchStart={onHeadDown}>
                {/* Cylinder body */}
                <rect x={-D} y={-R} width={D} height={R * 2} fill="#1b1b1b" rx="1"/>
                {/* Top highlight strip */}
                <rect x={-D + 2} y={-R} width={D - 4} height="4" rx="1" fill="#252525"/>
                {/* Bottom shadow strip */}
                <rect x={-D + 2} y={R - 4} width={D - 4} height="4" rx="1" fill="#141414"/>
                {/* Ring groove lines */}
                {[0.22, 0.47, 0.70, 0.90].map((t, i) => (
                  <line key={i}
                    x1={-D * t} y1={-R} x2={-D * t} y2={R}
                    stroke="#171717" strokeWidth="1" opacity=".9"
                  />
                ))}
                {/* Back cap (joint side) */}
                <ellipse cx={0} cy={0}        rx={ERX}       ry={R}       fill="#202020"/>
                <ellipse cx={0} cy={-R * .38} rx={ERX * .42} ry={R * .26} fill="#2c2c2c" opacity=".6"/>
                {/* Emitting face */}
                <ellipse cx={-D} cy={0} rx={ERX} ry={R} fill="#0b0b0b"/>
                {/* Concentric LED rings */}
                {[R, R * .76, R * .54, R * .33, R * .14].map((r, i) => (
                  <ellipse key={i}
                    cx={-D} cy={0}
                    rx={Math.max(1, ERX * r / R)} ry={r}
                    fill="none"
                    stroke={isOn ? lc.led : "#1a1a1a"}
                    strokeWidth={isOn ? (i === 4 ? 2.2 : 1.1) : 0.5}
                    opacity={isOn ? 0.95 - i * 0.08 : 0.3}
                    filter={isOn && i >= 3 ? "url(#lp-sg)" : undefined}
                  />
                ))}
                {/* Centre bright spot */}
                <ellipse cx={-D +1} cy={0}
                  rx={ERX * 0.9} ry={R * 0.9}
                  fill={isOn ? lc.led : "#0f0f0f"}
                  filter={isOn ? "url(#lp-glow)" : undefined}
                />
                {/* Face bloom when on */}
                {isOn && (
                  <ellipse cx={-D} cy={0}
                    rx={ERX * 1} ry={R * 1}
                    fill={`rgba(${lc.rgb},0.15)`}
                    filter="url(#lp-sg)"
                  />
                )}
                {/* Touch zone — top half of drum: tap to toggle */}
                <rect
                  x={-D - ERX} y={-R - 2}
                  width={D + ERX * 2} height={R + 2}
                  fill="transparent"
                  style={{ cursor: "pointer" }}
                  onClick={() => { if (!wasDrag.current) setIsOn(o => !o); }}
                />
              </g>

              
            </svg>

            {/* ─── CONTROLS: colour swatches + power ─── */}
            <div className="lp-controls" style={{margin:"15px 30px -15px 10px"}}>
              {LIGHTS.map((lgt, i) => (
                <button
                  key={i}
                  className={`lp-swatch${colorIdx === i && isOn ? " active" : ""}`}
                  style={{ "--sw": i === 0 ? "#d4e8ff" : i === 1 ? "#fff5cc" : "#ffd060" }}
                  onClick={() => { setColorIdx(i); if (!isOn) setIsOn(true); }}
                  title={lgt.label}
                />
              ))}
            </div>

            {/* ─── SPOT ANNOTATIONS ─── */}
            {SPOTS.map((spot) => (
              <div key={spot.id} className="h4-spot-wrap"
                style={{ left: `${spot.x}%`, top: `${spot.y}%` }}>
                <button
                  className={`h4-spot-btn${active === spot.id ? " active" : ""}`}
                  onClick={() => {
                    const isSame = active === spot.id;
                    const next = isSame ? null : spot.id;

                    // clear old timer
                    if (timeoutRef.current) {
                      clearTimeout(timeoutRef.current);
                    }

                    // set active
                    setActive(next);

                    // actions (independent 🔥)
                    if (spot.id === "magnetic") {
                      setDetached(next === "magnetic");
                    }

                    if (spot.id === "touch") {
                      setIsOn(o => !o);
                    }

                    // ⏱️ auto hide after 3 sec (ONLY if opening)
                    if (next) {
                      timeoutRef.current = setTimeout(() => {
                        setActive(null);
                      }, 3000);
                    }
                  }}
                  aria-label={`${active === spot.id ? "Close" : "View"} ${spot.title}`}
                >
                  {active === spot.id ? "×" : "+"}
                </button>
                {active === spot.id && (
                  <div className={`h4-spot-card h4-spot-card-${spot.align}`}>
                    <div className="h4-spot-title">{spot.title}</div>
                    <ul className="h4-spot-list">
                      {spot.specs.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="h4-stats">
        {STATS.map((s, i) => (
          <div key={i} className="h4-stat" style={{ animationDelay: `${i * 0.1 + 0.95}s` }}>
            <span className="h4-stat-val">{s.val}</span>
            <span className="h4-stat-lbl">{s.lbl}</span>
          </div>
        ))}
      </div>

      <div className="h4-scroll">
        <div className="h4-scroll-mouse"><div className="h4-scroll-dot" /></div>
        <span>Scroll</span>
      </div>
    </section>
  );
}
